"use client";

import { AreaChart } from "@/shared/ui/AreaChart";
import { BarChart } from "@/shared/ui/BarChart";
import type { ChartConfig } from "@/shared/ui/ChartContainer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import { useMemo, useState } from "react";
import type { OperationsParams } from "../../api/client";
import {
  useAvgPrepTimeQuery,
  useOrderThroughputQuery,
} from "../../api/queries";
import { OperationsReportsSummaryCards } from "./OperationsReportsSummaryCards";

type OperationsReportsContentProps = {
  filters?: OperationsParams;
  groupBy?: "hour" | "day";
  onGroupByChange?: (groupBy: "hour" | "day") => void;
};

export function OperationsReportsContent({
  filters,
  groupBy = "day",
  onGroupByChange,
}: OperationsReportsContentProps) {
  const [internalGroupBy, setInternalGroupBy] = useState<"hour" | "day">("day");
  const currentGroupBy = groupBy ?? internalGroupBy;
  const handleGroupByChange = onGroupByChange ?? setInternalGroupBy;

  const { data: avgPrepTimeData, isLoading: isLoadingPrepTime } =
    useAvgPrepTimeQuery(filters);

  const { data: throughputData, isLoading: isLoadingThroughput } =
    useOrderThroughputQuery({
      ...filters,
      groupBy: currentGroupBy,
    });

  // Get daily throughput for trend chart
  const { data: dailyThroughputData } = useOrderThroughputQuery({
    ...filters,
    groupBy: "day",
  });

  const throughputChartConfig: ChartConfig = {
    orderCount: {
      label: "ຈຳນວນອໍເດີ",
      color: "hsl(var(--chart-1))",
    },
  };

  const prepTimeTrendConfig: ChartConfig = {
    avgPrepTime: {
      label: "ເວລາເຕີມສະເລ່ຍ",
      color: "hsl(var(--chart-1))",
    },
  };

  const isLoading = isLoadingPrepTime || isLoadingThroughput;

  // Calculate overall average prep time
  const overallAvgPrepTime =
    avgPrepTimeData && avgPrepTimeData.length > 0
      ? avgPrepTimeData.reduce(
          (sum, item) => sum + item.avgPrepTimeMinutes * item.totalOrders,
          0,
        ) / avgPrepTimeData.reduce((sum, item) => sum + item.totalOrders, 0)
      : 0;

  // Calculate total orders
  const totalOrders =
    throughputData?.reduce((sum, item) => sum + item.orderCount, 0) ?? 0;

  // Calculate peak hour
  const peakHour = useMemo(() => {
    if (!throughputData || throughputData.length === 0) return null;

    // Find hour with max orders
    const hourlyData = throughputData.filter((item) =>
      item.period.includes(":"),
    );
    if (hourlyData.length === 0) return null;

    const maxItem = hourlyData.reduce((max, item) =>
      item.orderCount > max.orderCount ? item : max,
    );

    // Parse hour from format "YYYY-MM-DD HH24:00" or "HH24:00"
    let hourNum = 0;
    if (maxItem.period.includes(" ")) {
      // Format: "YYYY-MM-DD HH24:00"
      const timePart = maxItem.period.split(" ")[1];
      if (timePart) {
        const [hourStr] = timePart.split(":");
        hourNum = Number.parseInt(hourStr ?? "0", 10);
      }
    } else {
      // Format: "HH24:00"
      const [hourStr] = maxItem.period.split(":");
      hourNum = Number.parseInt(hourStr ?? "0", 10);
    }

    const nextHour = hourNum + 1;
    const period = hourNum >= 12 ? "PM" : "AM";
    const displayHour =
      hourNum > 12 ? hourNum - 12 : hourNum === 0 ? 12 : hourNum;
    const displayNextHour =
      nextHour > 12 ? nextHour - 12 : nextHour === 0 ? 12 : nextHour;

    return `${displayHour}-${displayNextHour} ${period}`;
  }, [throughputData]);

  // Calculate average orders per hour
  const avgOrdersPerHour = useMemo(() => {
    if (!throughputData || throughputData.length === 0) return 0;
    const hourlyData = throughputData.filter((item) =>
      item.period.includes(":"),
    );
    if (hourlyData.length === 0) return 0;
    const total = hourlyData.reduce((sum, item) => sum + item.orderCount, 0);
    return total / hourlyData.length;
  }, [throughputData]);

  // Format throughput data for chart
  const formattedThroughput =
    throughputData?.map((item) => ({
      period: item.period,
      orderCount: Number(item.orderCount) || 0,
      branch: item.branchName ?? "ທັງໝົດ",
    })) ?? [];

  // Format daily throughput for prep time trend (simplified - using order count as proxy)
  // In real implementation, we'd need daily avg prep time data
  const formattedPrepTimeTrend =
    dailyThroughputData?.slice(-7).map((item) => ({
      date: item.period,
      avgPrepTime: overallAvgPrepTime, // Placeholder - should be actual daily avg prep time
    })) ?? [];

  // Sort branches by avg prep time for ranking
  const branchRanking = useMemo(() => {
    if (!avgPrepTimeData) return [];
    return [...avgPrepTimeData]
      .filter((item) => item.branchName)
      .sort((a, b) => a.avgPrepTimeMinutes - b.avgPrepTimeMinutes)
      .slice(0, 5);
  }, [avgPrepTimeData]);

  // Calculate success rate (placeholder - would need actual data)
  const successRate = 98.4; // Placeholder
  const lateOrders = Math.round((totalOrders * (100 - successRate)) / 100);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="rounded-xl border bg-card">
              <CardContent className="p-6">
                <div className="h-10 animate-pulse rounded bg-muted" />
                <div className="mt-2 h-6 w-16 animate-pulse rounded bg-muted" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card className="rounded-xl border bg-card">
          <CardContent className="p-6">
            <div className="h-64 animate-pulse rounded bg-muted" />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <OperationsReportsSummaryCards
        avgPrepTime={overallAvgPrepTime}
        totalOrders={totalOrders}
        peakHour={peakHour}
        avgOrdersPerHour={avgOrdersPerHour}
        isLoading={isLoading}
      />

      {/* Main Content Grid */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* Left Column - Average Prep Time Trend */}
        <Card className="rounded-xl border bg-card md:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>ແນວໂນ້ມເວລາເຕີມສະເລ່ຍ</CardTitle>
            <div className="flex items-center gap-2 rounded-full bg-blue-500/10 px-3 py-1">
              <div className="size-2 rounded-full bg-blue-600" />
              <span className="font-medium text-blue-600 text-xs">
                ເປົ້າໝາຍ: 7.0 ນາທີ
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {formattedPrepTimeTrend.length === 0 ? (
              <div className="flex h-64 items-center justify-center text-muted-foreground">
                ບໍ່ມີຂໍ້ມູນສຳລັບການກອງທີ່ເລືອກ
              </div>
            ) : (
              <div className="h-64 w-full min-w-0" style={{ height: "256px" }}>
                <AreaChart
                  data={formattedPrepTimeTrend}
                  config={prepTimeTrendConfig}
                  xDataKey="date"
                  valueDataKey={["avgPrepTime"]}
                  xAxisTickFormatter={(value) => {
                    try {
                      const date = new Date(String(value));
                      if (Number.isNaN(date.getTime())) return String(value);
                      return date.toLocaleDateString("lo-LA", {
                        weekday: "short",
                      });
                    } catch {
                      return String(value);
                    }
                  }}
                  stacked={false}
                  className="h-full w-full"
                  margin={{ left: 12, right: 12, top: 12, bottom: 12 }}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right Column - Branch Ranking */}
        <Card className="rounded-xl border bg-card md:col-span-1">
          <CardHeader>
            <CardTitle>ອັນດັບສາຂາ</CardTitle>
          </CardHeader>
          <CardContent>
            {branchRanking.length === 0 ? (
              <div className="flex h-64 items-center justify-center text-muted-foreground">
                ບໍ່ມີຂໍ້ມູນສຳລັບການກອງທີ່ເລືອກ
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-4 border-b pb-2 font-medium text-muted-foreground text-sm">
                  <div>ສາຂາ</div>
                  <div className="text-center">ເວລາສະເລ່ຍ</div>
                  <div className="text-right">ອໍເດີ</div>
                </div>
                {branchRanking.map((item, index) => {
                  const isGood = item.avgPrepTimeMinutes < 8;
                  const isModerate =
                    item.avgPrepTimeMinutes >= 8 &&
                    item.avgPrepTimeMinutes < 10;
                  const colorClass = isGood
                    ? "text-green-600"
                    : isModerate
                      ? "text-yellow-600"
                      : "text-red-600";

                  return (
                    <div
                      key={`${item.branchId}-${index}`}
                      className="grid grid-cols-3 gap-4 py-2"
                    >
                      <div className="font-medium">{item.branchName}</div>
                      <div
                        className={`text-center font-semibold ${colorClass}`}
                      >
                        {item.avgPrepTimeMinutes.toFixed(1)} ນາທີ
                      </div>
                      <div className="text-right">
                        {item.totalOrders.toLocaleString("lo-LA")}
                      </div>
                    </div>
                  );
                })}
                <div className="pt-2">
                  <button
                    type="button"
                    className="text-primary text-sm hover:underline"
                  >
                    ເບິ່ງທັງໝົດ
                  </button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Left Column Bottom - Order Throughput Analysis */}
        <Card className="rounded-xl border bg-card md:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle>ການວິເຄາະຈຳນວນອໍເດີ</CardTitle>
              <p className="text-muted-foreground text-sm">
                ການແຈກຢາຍປະລິມານຕາມຊ່ວງເວລາ
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant={currentGroupBy === "hour" ? "default" : "outline"}
                size="sm"
                onClick={() => handleGroupByChange("hour")}
              >
                ຕາມຊົ່ວໂມງ
              </Button>
              <Button
                variant={currentGroupBy === "day" ? "default" : "outline"}
                size="sm"
                onClick={() => handleGroupByChange("day")}
              >
                ຕາມວັນ
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {formattedThroughput.length === 0 ? (
              <div className="flex h-64 items-center justify-center text-muted-foreground">
                ບໍ່ມີຂໍ້ມູນສຳລັບການກອງທີ່ເລືອກ
              </div>
            ) : (
              <div className="h-64 w-full min-w-0" style={{ height: "256px" }}>
                <BarChart
                  data={formattedThroughput}
                  config={throughputChartConfig}
                  xDataKey="period"
                  valueDataKey={["orderCount"]}
                  verticalGrid={true}
                  className="h-full w-full"
                  margin={{ left: 12, right: 12, top: 12, bottom: 12 }}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Right Column Bottom - Metrics */}
        <Card className="rounded-xl border bg-card md:col-span-1">
          <CardHeader>
            <CardTitle>ຕົວຊີ້ວັດສະຫຼຸບ</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* TOTAL THROUGHPUT */}
            <div>
              <div className="font-medium text-muted-foreground text-sm">
                ຈຳນວນອໍເດີທັງໝົດ
              </div>
              <div className="mt-1 font-bold text-3xl tabular-nums">
                {totalOrders.toLocaleString("lo-LA")}
              </div>
              <div className="text-muted-foreground text-xs">ອໍເດີທີ່ປະມວນຜົນມື້ນີ້</div>
            </div>

            {/* SUCCESS RATE */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <div className="font-medium text-muted-foreground text-sm">
                  ອັດຕາສຳເລັດ
                </div>
                <div className="font-semibold tabular-nums">{successRate}%</div>
              </div>
              <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full bg-green-600 transition-[width] duration-500"
                  style={{ width: `${successRate}%` }}
                />
              </div>
            </div>

            {/* LATE ORDERS */}
            <div>
              <div className="font-medium text-muted-foreground text-sm">
                ອໍເດີຊັ້ນ
              </div>
              <div className="mt-1 font-bold text-2xl text-red-600 tabular-nums">
                {lateOrders}
              </div>
              <div className="text-muted-foreground text-xs">
                {((lateOrders / totalOrders) * 100).toFixed(1)}% ຂອງປະລິມານທັງໝົດ
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom Section - Throughput Details */}
      <Card className="rounded-xl border bg-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>ລາຍລະອຽດຈຳນວນອໍເດີ (ຕາມສ່ວນ)</CardTitle>
        </CardHeader>
        <CardContent>
          {formattedThroughput.length === 0 ? (
            <div className="flex h-32 items-center justify-center text-muted-foreground">
              ບໍ່ມີຂໍ້ມູນສຳລັບການກອງທີ່ເລືອກ
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b text-muted-foreground text-sm">
                    <th className="pb-3 text-left font-medium">ຊ່ວງເວລາ</th>
                    <th className="pb-3 text-right font-medium">ອໍເດີແອັບ</th>
                    <th className="pb-3 text-right font-medium">ມາຮ້ານ</th>
                    <th className="pb-3 text-right font-medium">ສຳເລັດສະເລ່ຍ</th>
                    <th className="pb-3 text-right font-medium">ສະຖານະ</th>
                  </tr>
                </thead>
                <tbody>
                  {formattedThroughput.slice(0, 10).map((item, index) => {
                    // Placeholder data - in real implementation, we'd need segment data
                    const appOrders = Math.round(item.orderCount * 0.7);
                    const walkinOrders = item.orderCount - appOrders;
                    const avgCompletion = overallAvgPrepTime.toFixed(1);
                    const isPeak =
                      index === Math.floor(formattedThroughput.length / 2);

                    return (
                      <tr key={item.period} className="border-b">
                        <td className="py-3">{item.period}</td>
                        <td className="py-3 text-right tabular-nums">
                          {appOrders}
                        </td>
                        <td className="py-3 text-right tabular-nums">
                          {walkinOrders}
                        </td>
                        <td className="py-3 text-right tabular-nums">
                          {avgCompletion} ນາທີ
                        </td>
                        <td className="py-3 text-right">
                          <span
                            className={`inline-flex items-center gap-1 ${
                              isPeak ? "text-red-600" : "text-green-600"
                            }`}
                          >
                            <span className="size-2 rounded-full bg-current" />
                            {isPeak ? "ສູງສຸດ" : "ເໝາະສົມ"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
